import { useState } from 'react';
import { DatePicker, Modal } from 'antd';
import {
  numToStr,
  toMillisecond,
  toSecond,
  getUnitOfSpecie,
} from '../../../lib/common.helpers';
import { IMusselLineResource } from '../../../entities/farms.entities';
import TimeTrackInput, {
  toSecTimeTracks,
} from '../../shared/input/TimeTrackInput';
import { Button, CloseIcon, Dropdown, Input, Subtitle } from '../../shared';
import { useDispatch, useSelector } from 'react-redux';
import FilesWall from '../../shared/pictures-wall/FilesWall';
import { sendSingleRequest } from '../../../apis';
import { showFeedback } from '../../../store/ui/ui.actions';
import { ICustomValue } from '../../../entities/automation.entities';
import { loadFarmsData } from '../../../store/farms/farms.actions';
import { IFileWall, ITimeRange } from '../../../entities/general.entities';
import { selectLang } from '../../../store/ui/ui.selector';
import { translate } from '../../../lib/lang.helper';
import {
  SelectDetailedBudgetEnabled,
  SelectInventoriesByFarmID,
  SelectIsBudgetVisible,
  SelectSeasonsByFarmID,
  SelectSeedingFields,
} from '../../../store/extra/extra.selector';
import moment from 'moment';
import { inventoryLabel } from '../../../lib/farm.helpers';
import { IExpenseRequest } from '../../../entities/budget.entities';
import OperatorBudgetForm from '../OperatorBudgetForm';
import CustomValuesForm from '../../farm-util/CustomValuesForm';
import { selectAccount } from '../../../store/auth/auth.selector';
const { RangePicker } = DatePicker;

interface IFormData {
  season_name: string | null;
  line_length: number | null;
  drop?: number;
  submersion?: number;
  spacing?: number;
  comment: string | null;
  image_pdf_files?: IFileWall[];
  time_tracks?: ITimeRange[];

  custom_values?: ICustomValue[];

  cost?: number | null;
  expenses?: IExpenseRequest[];
  budget_unit_price?: number;
  budget_unit_amount?: number;
}

const defaultForm: IFormData = {
  season_name: null,
  line_length: null,
  drop: 0,
  submersion: 0,
  spacing: 0,
  comment: null,
  image_pdf_files: [],
  time_tracks: [],
};

interface Props {
  visible: boolean;
  title: string;
  lineData: IMusselLineResource;
  onCancel: () => void;
  onConfirm: (data: any) => void;
  data?: any;
  overConfirm?: (data: any) => Promise<any>;
}

const MusselCatchSpatModal = ({
  visible,
  title,
  lineData,
  onCancel,
  onConfirm,
  data: paramData,
  overConfirm,
}: Props) => {
  const dispatch = useDispatch<any>();

  const showBudget = SelectIsBudgetVisible(lineData.farm_id);
  const customFields = SelectSeedingFields(lineData.farm_id);
  const inventories = SelectInventoriesByFarmID(lineData.farm_id);
  const detailedBudgetEnabled = SelectDetailedBudgetEnabled(lineData.farm_id);
  const lang = useSelector(selectLang);
  const unitType = useSelector(selectAccount)?.display_unit;
  const seasons = SelectSeasonsByFarmID(lineData.farm_id)
    .map(x => x.name)
    .filter((v, i, a) => a.indexOf(v) === i);

  const [disabled, setDisabled] = useState(false);
  const [startDate, setStartDate] = useState(
    paramData && paramData.planned_date_seed
      ? toMillisecond(paramData.planned_date_seed)
      : 0,
  );
  const [endDate, setEndDate] = useState(
    paramData && paramData.planned_date_harvest
      ? toMillisecond(paramData.planned_date_harvest)
      : 0,
  );
  const [error, setError] = useState<string | null>(null);
  const [fieldData, setFieldData] = useState<IFormData>(
    paramData
      ? {
          ...defaultForm,
          ...paramData,
          line_length: paramData.line_length,
          drop: paramData.drop,
          submersion: paramData.submersion,
          spacing: paramData.spacing,
          time_tracks: paramData.time_tracks
            ? paramData.time_tracks.map((x: any) => ({
                start_time: toMillisecond(x.start_time),
                finish_time: toMillisecond(x.finish_time),
              }))
            : [],
        }
      : {
          ...defaultForm,
          line_length: lineData.backbone,
          expenses: detailedBudgetEnabled
            ? [{ name: '', quantity: null, unit_price: null, amount: null }]
            : undefined,
        },
  );
  const [pickedInventories, setPickedInventories] = useState<any[]>(
    paramData?.picked_inventories ?? [],
  );

  const fieldValid = () => {
    if (startDate === 0 || endDate === 0) {
      setError(translate(lang, 'Please select range'));
      return null;
    }
    if (!fieldData.line_length) {
      setError(translate(lang, '_catch_spat_length_empty'));
      return null;
    }
    for (let px of pickedInventories) {
      const ivt = inventories.find(x => x.id === px.id);
      if (!ivt) {
        setError(translate(lang, '_inv_found_2'));
        return null;
      }
      if (isNaN(Number(px.quantity)) || Number(px.quantity) <= 0) {
        setError(translate(lang, '_invalid_ivt_qty', ivt.name ?? ''));
        return null;
      }
      if (Number(px.quantity) > ivt.available_quantity) {
        setError(
          translate(
            lang,
            '_seed_floats_over',
            px.quantity,
            ivt.name ?? '',
            ivt.available_quantity,
          ),
        );
        return null;
      }
    }
    let res = {
      planned_date_seed: toSecond(startDate),
      planned_date_harvest: toSecond(endDate),
      line_id: lineData.id,
      ...fieldData,
      picked_inventories:
        pickedInventories.length > 0 ? pickedInventories : undefined,
      expenses: fieldData.expenses?.filter(x => x.name && x.amount),
      is_catch_spat: true,
    };
    if (res.time_tracks && res.time_tracks.length > 0) {
      const tt = toSecTimeTracks(res.time_tracks);
      if (!tt) {
        setError(translate(lang, '_invalid_time_tracks_info'));
        return null;
      }
      res.time_tracks = tt;
    }
    res.custom_values = fieldData.custom_values?.filter(x => !!x.field_val);

    return res;
  };
  const onRangeChange = (dates: any, dateStrings: any) => {
    setStartDate(dates[0].toDate().getTime());
    setEndDate(dates[1].toDate().getTime());
    setError(null);
  };
  const updateField = (key: keyof IFormData, val: any) => {
    if (['spacing', 'submersion', 'drop', 'line_length'].includes(key)) {
      val = val.length <= 0 ? null : Number(val);
    }
    setFieldData({ ...fieldData, [key]: val });
  };
  const selectInventoryIds = (val: string[]) => {
    let pArr = val.map(x => ({ id: parseInt(x), quantity: '' }));
    for (let p of pickedInventories) {
      let i = pArr.findIndex(x => x.id === p.id);
      if (i > -1) pArr[i].quantity = p.quantity;
    }
    setPickedInventories(pArr);
  };
  const updateInventoryQuantity = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const {
      value,
      dataset: { type },
    } = event.target;
    let pickedArr = [...pickedInventories];
    pickedArr[parseInt(type ?? '0')].quantity = value;
    setPickedInventories(pickedArr);
  };
  const handleConfirmClick = async () => {
    const form = fieldValid();
    if (!form) return;

    setDisabled(true);
    if (!!overConfirm) {
      const ovc = await overConfirm(form);
      if (ovc !== true) {
        setDisabled(false);
        if (ovc.message) {
          dispatch(
            showFeedback({
              isMessageModal: true,
              type: 'error',
              message: translate(lang, ovc.message),
            }),
          );
        }
      }
      return;
    }
    const res = await sendSingleRequest(
      form,
      'POST',
      'api/farm/line/catch-spat',
      true,
    );
    if (res.status) {
      dispatch(loadFarmsData());
      onConfirm(res.data);
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: translate(lang, res.data?.message ?? 'Success'),
        }),
      );
    } else {
      setError(translate(lang, res.data?.message ?? 'Request failed'));
      setDisabled(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      footer={null}
      closable
      closeIcon={<CloseIcon />}
      width={650}
    >
      <div className='wrap'>
        <div className='d-flex align-center mb-32'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {title}
          </Subtitle>
        </div>
        <div>
          <label>{translate(lang, '_catch_spat_range')}</label>
          <div className='mt-16 mb-32'>
            <RangePicker
              defaultValue={
                startDate && endDate
                  ? [moment(startDate), moment(endDate)]
                  : undefined
              }
              onChange={onRangeChange}
              format={'DD/MM/yyyy'}
            />
          </div>
          {error && (
            <div
              className='invalid-form'
              style={{ fontSize: '15px', textAlign: 'center' }}
            >
              <div className='invalid-feedback'>{error}</div>
            </div>
          )}
          <div className='mb-17'>
            <Dropdown
              label={translate(lang, 'Catch Spat Season')}
              options={seasons.map(s => ({ id: s, value: s, label: s }))}
              value={fieldData.season_name ?? undefined}
              onChange={v => updateField('season_name', v)}
            />
          </div>
          {fieldData.time_tracks && (
            <TimeTrackInput
              data={fieldData.time_tracks}
              onChange={t => updateField('time_tracks', t)}
              baseDate={startDate}
            />
          )}
          <div className='mb-16'>
            <Input
              label={translate(lang, 'Catch spat length')}
              type='number'
              value={numToStr(fieldData.line_length)}
              dataType='line_length'
              onChange={e => updateField('line_length', e.target.value)}
              required
              unit={getUnitOfSpecie(unitType, 'distance_m')}
            />
          </div>
          <div className='mb-16'>
            <Input
              label={translate(lang, 'Drop')}
              type='number'
              dataType='drop'
              value={numToStr(fieldData.drop)}
              unit={getUnitOfSpecie(unitType, 'distance_m')}
              required
              onChange={e => updateField('drop', e.target.value)}
            />
          </div>
          <div className='d-flex pb-17'>
            <div className='mr-12 w-100'>
              <Input
                label={translate(lang, 'Submersion')}
                value={numToStr(fieldData.submersion)}
                dataType='submersion'
                type='number'
                unit={getUnitOfSpecie(unitType, 'distance_m')}
                required
                onChange={e => updateField('submersion', e.target.value)}
              />
            </div>
            <div className='ml-12 w-100'>
              <Input
                label={translate(lang, 'Spacing')}
                type='number'
                value={numToStr(fieldData.spacing)}
                dataType='spacing'
                unit={getUnitOfSpecie(unitType, 'size_mm')}
                required
                onChange={e => updateField('spacing', e.target.value)}
              />
            </div>
          </div>
          <CustomValuesForm
            fields={customFields}
            values={fieldData.custom_values ?? []}
            onChange={d => setFieldData(prv => ({ ...prv, custom_values: d }))}
            disabled={disabled}
          />
          {!!showBudget && (
            <OperatorBudgetForm
              detailedBudgetEnabled={detailedBudgetEnabled}
              form={fieldData}
              onChange={setFieldData}
            />
          )}
          <div className='mb-17'>
            <Dropdown
              label={translate(lang, 'Select inventory')}
              placeholder={translate(lang, 'Select inventory')}
              mode='multiple'
              options={inventories.map(x => ({
                id: x.id.toString(),
                value: x.id.toString(),
                label: inventoryLabel(x),
              }))}
              value={pickedInventories.map(x => x.id.toString()) as any}
              onChange={(val: any) => selectInventoryIds(val)}
            />
          </div>
          {pickedInventories.length > 0 && (
            <div className='mb-17'>
              {pickedInventories.map((item, idx) => (
                <Input
                  className='mb-15'
                  key={item.id}
                  type='number'
                  label={inventoryLabel(
                    inventories.find(t => t.id === item.id),
                  )}
                  placeholder={translate(
                    lang,
                    'Maximum value is %s',
                    inventories.find(t => t.id === item.id)
                      ?.available_quantity ?? '',
                  )}
                  value={numToStr(item.quantity)}
                  dataType={idx.toString()}
                  onChange={updateInventoryQuantity}
                  max={
                    inventories.find(t => t.id === item.id)?.available_quantity
                  }
                />
              ))}
            </div>
          )}
          <div className='mt-17 mb-17'>
            <Input
              label={translate(lang, 'Comment')}
              placeholder={translate(lang, 'Comment')}
              type='textarea'
              value={fieldData.comment ?? ''}
              onChange={e => updateField('comment', e.target.value)}
            />
          </div>
          <div className='mb-17'>
            <FilesWall
              label={translate(lang, 'Images & PDF Files')}
              fileList={fieldData.image_pdf_files}
              onChange={v => updateField('image_pdf_files', v)}
            />
          </div>
        </div>
      </div>
      <div className='modal-button d-flex justify-end align-center'>
        <Button
          width={'small'}
          size={2}
          type='fill'
          color='green'
          className='rsp-btn ml-16'
          onClick={handleConfirmClick}
          disabled={disabled}
        >
          {translate(lang, 'Confirm')}
        </Button>
      </div>
    </Modal>
  );
};

export default MusselCatchSpatModal;
